import type {
  LayoutSettings,
  LayoutItem,
  InteractiveOptions,
  UserData,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import type { MelodicContourQuestion } from '../uTheory.Skills/melodicContourQuestions';
import MelodicContourQuestions from '../uTheory.Skills/melodicContourQuestions';
import utils from '../utils';
import ProtoNotation from '../uTheory.Notation/protoNotation';

interface MelodicCountourInteractiveLayout extends LayoutSettings {
  protoNotation: LayoutItem;
}

interface MelodicContourInteractiveOptions extends InteractiveOptions {
  level: number;
}

/**
 * Drag circles to create the correct contour for an aural melody
 */
export default class MelodicContourDictation extends InteractivePrototype {
  declare layout: MelodicCountourInteractiveLayout;

  declare options: MelodicContourInteractiveOptions;

  protoNotationDiv: HTMLDivElement;

  pn: ProtoNotation;

  declare questions: MelodicContourQuestion[];

  declare currentQuestion: MelodicContourQuestion;

  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions, userData: UserData) {
    super(displayDiv, options, userData);

    this.layout = {
      protoNotation: {
        percent: 85,
        visible: true,
      },
    };
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: InteractiveOptions, userData: UserData) {
    this.settings = {
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a series of notes, ' +
          'move the circles to match the contour of the notes.</p>',
        callback: this.playMusic,
        //          thisArg: this
      },
      instructions: true,
      checkMark: {},
      letters: {
        letterButtons: false,
        checkButton: true,
      },
      player: {},
      progressTracker: true,
    };

    return super.init(displayDiv, options, userData);
  }

  createCustomDivs() {
    this.createDiv(
      'protoNotationDiv',
      {
        overflow: 'hidden',
        textAlign: 'center',
      },
      true
    );
    this.layout.protoNotation.div = this.protoNotationDiv;
    this.layout.protoNotation.callback = this.resizeProtoNotation.bind(this);
    this.displayDiv.insertBefore(this.protoNotationDiv, this.lettersDiv);
    const pn = new ProtoNotation([0, 1, 2, 3]);
    this.protoNotationDiv.appendChild(pn.svg);
    pn.makeDraggable();
    this.pn = pn;
  }

  resizeProtoNotation(heightAvailable: number) {
    this.protoNotationDiv.style.height = '';
    this.protoNotationDiv.style.position = 'relative';
    this.pn.svg.style.width = '97%';
    let percent = 100;

    while (heightAvailable - this.protoNotationDiv.clientHeight < 0) {
      percent -= 2;
      this.pn.svg.style.width = `${percent}%`;
    }

    this.pn.resizeOverlay();

    this.protoNotationDiv.style.height = `${this.protoNotationDiv.clientHeight}px`;
    return heightAvailable - this.protoNotationDiv.clientHeight;
  }

  generateQuestions() {
    const maxQuestions = this.options.maxQuestions || 8;
    const qg = new MelodicContourQuestions();

    let level;
    if (this.options.level) {
      level = this.options.level;
    } else if (this.options.skills && this.userData && this.userData.progress) {
      level = MelodicContourQuestions.getRecommendedLevel(this.userData.progress);
    } else if (this.options.skills) {
      // Pick an appropriate level for them to work on:
      const levels = MelodicContourQuestions.skillsToLevels(this.options.skills)
        .reverse()
        .slice(-2);
      level = utils.shuffle(levels).slice(-1)[0];
    } else {
      level = Math.ceil(Math.random() * 6);
    }

    this.questions = qg.getDictationQuestions(level, maxQuestions);
    this.questionPrompt = 'Drag the circles to match the contour you hear.';
    // Generally, this sets four variables:
    // this.questions = [], an array of objects representing the questions
    // & answers:
    //       { text : string representation for display,
    //         answer : answer object for comparison,
    //         skills : integer or array of integers of unique_id's for skills
    //       }
    // this.questionPrompt, the text displayed to the user, with {question} replaced by...
    // this.maxQuestions = int.
    // this.questionNumber = 0;
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  newQuestion() {
    super.newQuestion();
    const len = this.currentQuestion.answer.length;
    this.pn.draw('1'.repeat(len));
    if (this.questionNumber !== 0) {
      window.setTimeout(() => this.playMusic(), 500);
    }
  }

  gameOver() {
    super.gameOver();
    this.protoNotationDiv.style.display = 'none';
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
  }

  playMusic(delay = 0) {
    this.player.stop();
    const duration = 0.6;
    this.currentQuestion.notes.forEach((midi, index, arr) => {
      const startTime = duration * index + delay;
      let stopTime = index === arr.length - 1 ? duration * (index + 1) + 2 : duration * (index + 1);
      stopTime += delay;
      this.player.play(midi, { startTime, stopTime, velocity: 0.8 });
    }, this);
  }

  displayInstructions() {
    super.displayInstructions();
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    const guess = this.pn.getAnswerArray().join('');
    if (this.currentQuestion.answer === guess) {
      this.rightAnswer(500);
    } else {
      this.wrongAnswer();
    }
  }

  wrongAnswer() {
    this.playMusic(0.6);
    super.wrongAnswer();
  }

  // Not implemented
  // ## CheckPoint Mode Questions:
  // newCheckpointQuestion(questionString) {
  //   return this.loadingPromise.then(() => {
  //     this.question = questionString;
  //     const questionText = 'What is the name of the note below?';
  //     this.instructionsDiv.innerHTML = questionText;

  //     this.checkpointQuestion = {};
  //     return Promise.resolve(this.currentQuestion);
  //   });
  // }

  // Not implemented
  // // For checkpoint tests, the answer & data from the current question is
  // // logged via a callback.
  // getAnswerAndData() {
  //   const correct = undefined;
  //   const skill = undefined;
  //   const answerAndData = {
  //     question: this.question,
  //     questionType: 'MelodicContourDictation',
  //     questionText: this.instructionsDiv.innerHTML,
  //     questionSVG: null,
  //     studentAnswerText: null,
  //     studentAnswerSVG: null,
  //     correctAnswerText: null,
  //     rightAnswers: correct ? 1 : 0,
  //     wrongAnswers: correct ? 0 : 1,
  //     skills: skill,
  //   };
  //   return answerAndData;
  // }

  static registry = {
    description: 'Draw the contour you hear',
    skills: [
      'contour_basic',
      'contour_high_low',
      'contour_high_medium_low_easy',
      'contour_high_medium_low_hard',
      'contour_four_level_easy',
      'contour_four_level_hard',
    ],
  };
}
